import axios from 'axios';
import { getApiBaseUrl, getApiVersion } from '@/config/environment';
import { toast } from 'react-hot-toast';

export const API_BASE = `/api/${getApiVersion()}`;

// Create axios instance with dynamic base URL
const apiClient = axios.create({
  baseURL: getApiBaseUrl(),
  timeout: 30000, // Increased to 30 seconds for cloud database latency
  withCredentials: true, // Send cookies with all requests (equivalent to credentials: 'include')
  headers: {
    'Content-Type': 'application/json',
  },
});

// Update base URL when environment changes
export const updateApiBaseUrl = (newBaseUrl: string) => {
  apiClient.defaults.baseURL = newBaseUrl;
};

// Log the initial API base URL

// Request interceptor to add auth token
apiClient.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    // Debug logging - show full URL for first few requests
    const fullUrl = config.baseURL + config.url;

    // Debug logging for booking-related requests
    if (config.url?.includes('/bookings')) {
    }

    // Debug logging for admin staff requests
    if (config.url?.includes('/admin/staff') || config.url?.includes('/admin/system-roles')) {
    }

    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor to handle errors
apiClient.interceptors.response.use(
  (response) => {
    return response;
  },
  (error) => {
    if (error.response?.status === 401) {
      // Extract error information from response
      const responseData = error.response?.data;

      // Handle different response structures
      // Structure 1: { success: false, error: { code, message } }
      // Structure 2: { code, message }
      // Structure 3: { message }
      const errorInfo = responseData?.error || responseData;
      const errorCode = errorInfo?.code || responseData?.code;

      // Extract message - could be nested
      let errorMessage = errorInfo?.message || responseData?.message || errorInfo;

      // If errorMessage is still an object with nested message, extract it
      if (typeof errorMessage === 'object' && errorMessage?.message) {
        errorMessage = errorMessage.message;
      }

      console.error('🚫 401 UNAUTHORIZED:', {
        url: error.config?.url,
        fullUrl: error.config?.baseURL + error.config?.url,
        method: error.config?.method?.toUpperCase(),
        hasAuthHeader: !!error.config?.headers?.Authorization,
        authHeaderPreview: error.config?.headers?.Authorization?.substring(0, 30) + '...',
        responseData,
        errorCode,
        errorMessage,
        errorMessageStr: typeof errorMessage === 'string'
          ? errorMessage.toLowerCase()
          : JSON.stringify(errorMessage || '').toLowerCase(),
        timestamp: new Date().toISOString()
      });

      // Convert error message to string for checking
      const errorMessageStr = typeof errorMessage === 'string'
        ? errorMessage.toLowerCase()
        : JSON.stringify(errorMessage || '').toLowerCase();

      // Check if this is a permission/authorization error (not authentication)
      const isPermissionError =
        errorMessageStr.includes('permission') ||
        errorMessageStr.includes('insufficient') ||
        errorMessageStr.includes('required') ||
        errorMessageStr.includes('forbidden') ||
        errorMessageStr.includes('not authorized') ||
        errorMessageStr.includes('access denied') ||
        errorCode === 'INSUFFICIENT_PERMISSIONS' ||
        errorCode === 'ACCESS_DENIED' ||
        errorCode === 'FORBIDDEN';

      // Special handling for invitations endpoint - might be a backend configuration issue
      const isInvitationsEndpoint = error.config?.url?.includes('/admin/invitations');


      // If permission error, don't logout - let component handle it
      if (isPermissionError) {
        return Promise.reject(error);
      }

      // If invitations endpoint error, don't auto-logout to prevent losing session
      if (isInvitationsEndpoint) {
        return Promise.reject(error);
      }

      // Check if this is an authentication error (expired/invalid token)
      // Special case: "Invalid token: missing user ID" indicates a backend token parsing issue
      const isMissingUserIdError = errorMessageStr.includes('missing user id');

      // If it's a missing user ID error on admin endpoints, treat it as a backend configuration issue
      if (isMissingUserIdError && error.config?.url?.includes('/admin/')) {
        console.error('🔴 BACKEND TOKEN ISSUE: Token is missing userId claim. This is a backend JWT configuration issue.');
        // Don't logout, let the component handle this
        return Promise.reject(error);
      }

      const isAuthenticationError =
        !isMissingUserIdError && (
          errorCode === 'USER_DELETED' ||
          errorCode === 'ACCOUNT_SUSPENDED' ||
          errorCode === 'ACCOUNT_BANNED' ||
          errorCode === 'ACCOUNT_DISABLED' ||
          errorCode === 'TOKEN_EXPIRED' ||
          errorCode === 'INVALID_TOKEN' ||
          errorCode === 'NO_TOKEN' ||
          (errorMessageStr.includes('token') && (
            errorMessageStr.includes('expired') ||
            errorMessageStr.includes('malformed')
          ))
        );

      // Only logout for authentication errors
      if (isAuthenticationError) {
        console.error('❌ Authentication error - logging out');

        // Set logout reason and show toast notification
        let logoutMessage: string;
        if (errorCode === 'USER_DELETED') {
          logoutMessage = 'Your account has been deactivated. Please contact support for more information.';
        } else if (errorCode === 'ACCOUNT_SUSPENDED') {
          logoutMessage = 'Your account has been suspended. Please contact support.';
        } else if (errorCode === 'ACCOUNT_BANNED') {
          logoutMessage = 'Your account has been permanently banned.';
        } else if (errorCode === 'ACCOUNT_DISABLED') {
          logoutMessage = 'Your account has been disabled. Please contact support.';
        } else {
          logoutMessage = 'Your session has expired. Please log in again.';
        }

        // Store for login page
        localStorage.setItem('logoutReason', logoutMessage);

        // Show immediate notification to user
        toast.error(logoutMessage, {
          duration: 4000,
          position: 'top-center',
        });

        // Clear all auth data
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        localStorage.removeItem('userId');

        // Redirect to login after short delay to let user see the toast
        setTimeout(() => {
          window.location.href = '/auth/login';
        }, 1500);
      } else {
        // Unknown 401 error - log but don't logout
      }
    }

    // Log error for debugging
    console.error('API Error:', error.response?.data || error.message);

    return Promise.reject(error);
  }
);

export default apiClient;
